import { t } from 'i18next';
import { isEmpty } from 'lodash';
import React, { useState } from 'react';
import PrimaryButton from '../../../components/buttons/PrimaryButton';
import SecondaryButton from '../../../components/buttons/SecondaryButton';
import ReportLocationEventBottomDrawer from '../../../components/drawer/ReportLocationEventBottomDrawer';
import ReportLocationEventBox from '../../../components/offlineLocation/ReportLocationEventBox';
import { LocationEvent } from '../../../domain/models/locations/LocationEvent';
import { snakeToCamel } from '@pocapan/shared/utils/TextUtil';
import { IGoodsItem } from '../../../domain/models/locations/GoodsByCategory';
import BaseToastMessageBox from '../../../components/toast/BaseToastMessageBox';
import CheckIcon from '../../../components/icons/CheckIcon';
import { useToast } from '@chakra-ui/react';
interface EventGoodsProps {
  eventDetail: LocationEvent;
}

const EventGoodsByCategory: React.FC<EventGoodsProps> = ({ eventDetail }) => {
  enum GoodsCategory {
    ALL = 'all',
    EVENT = 'event',
    PHOTO = 'photo',
    STORAGE = 'storage',
    ACCESSORY = 'accessory',
    DECORATION = 'decoration'
  }

  const [selectedCategory, setSelectedCategory] = useState<GoodsCategory>(GoodsCategory.ALL);
  const [isOpenReportDrawer, setIsOpenReportDrawer] = useState<boolean>(false);

  const toast = useToast();

  const showReportToastMessage = () => {
    toast({
      position: 'top',
      duration: 2000,
      render: () => (
        <BaseToastMessageBox
          position={'top'}
          startElement={
            <div className="flex items-center justify-center rounded-full w-[15px] h-[15px] bg-onSuccess-light dark:bg-onSuccess-dark">
              <CheckIcon className="fill-pure-white w-[15px] h-[15px]" />
            </div>
          }
          message={t('ReportLocationEventBottomDrawer.toastMessage')}
        />
      )
    });
  };

  return (
    <div className="px-[20px]">
      <div className="flex flex-col py-[10px] gap-[4px]">
        <div className="text-[15px] leading-[18px] font-semibold text-main-light-2 dark:text-main-dark-2">
          {t('OfflineEventDetail.eventGoodsByCategoryTitle')}
        </div>
        <div className="text-[13px] leading-[18px] font-normal text-main-light-4 dark:text-main-dark-4">
          {t('OfflineEventDetail.eventGoodsGiftTypesDescription')}
        </div>
      </div>
      <div className="flex items-center gap-[6px] my-[16px]">
        {selectedCategory === GoodsCategory.ALL ? (
          <PrimaryButton
            text={t('GoodsCategory.all')}
            disabled={false}
            icon={null}
            fontSize="13px"
            onClick={() => setSelectedCategory(GoodsCategory.ALL)}
          />
        ) : (
          <SecondaryButton
            text={t('GoodsCategory.all')}
            disabled={false}
            icon={null}
            leftIcon={null}
            fontSize="13px"
            onClick={() => setSelectedCategory(GoodsCategory.ALL)}
          />
        )}
        {!isEmpty(eventDetail.goodsByCategory.photo) &&
          (selectedCategory === GoodsCategory.PHOTO ? (
            <PrimaryButton
              key={GoodsCategory.PHOTO}
              text={t('GoodsCategory.photo')}
              disabled={false}
              icon={null}
              fontSize="13px"
              onClick={() => setSelectedCategory(GoodsCategory.PHOTO)}
            />
          ) : (
            <SecondaryButton
              key={GoodsCategory.PHOTO}
              text={t('GoodsCategory.photo')}
              disabled={false}
              icon={null}
              leftIcon={null}
              fontSize="13px"
              onClick={() => setSelectedCategory(GoodsCategory.PHOTO)}
            />
          ))}
        {!isEmpty(eventDetail.goodsByCategory.storage) &&
          (selectedCategory === GoodsCategory.STORAGE ? (
            <PrimaryButton
              key={GoodsCategory.STORAGE}
              text={t('GoodsCategory.storage')}
              disabled={false}
              icon={null}
              fontSize="13px"
              onClick={() => setSelectedCategory(GoodsCategory.STORAGE)}
            />
          ) : (
            <SecondaryButton
              key={GoodsCategory.STORAGE}
              text={t('GoodsCategory.storage')}
              disabled={false}
              icon={null}
              leftIcon={null}
              fontSize="13px"
              onClick={() => setSelectedCategory(GoodsCategory.STORAGE)}
            />
          ))}
        {!isEmpty(eventDetail.goodsByCategory.decoration) &&
          (selectedCategory === GoodsCategory.DECORATION ? (
            <PrimaryButton
              key={GoodsCategory.DECORATION}
              text={t('GoodsCategory.decoration')}
              disabled={false}
              icon={null}
              fontSize="13px"
              onClick={() => setSelectedCategory(GoodsCategory.DECORATION)}
            />
          ) : (
            <SecondaryButton
              key={GoodsCategory.DECORATION}
              text={t('GoodsCategory.decoration')}
              disabled={false}
              icon={null}
              leftIcon={null}
              fontSize="13px"
              onClick={() => setSelectedCategory(GoodsCategory.DECORATION)}
            />
          ))}
        {!isEmpty(eventDetail.goodsByCategory.event) &&
          (selectedCategory === GoodsCategory.EVENT ? (
            <PrimaryButton
              key={GoodsCategory.EVENT}
              text={t('GoodsCategory.event')}
              disabled={false}
              icon={null}
              fontSize="13px"
              onClick={() => setSelectedCategory(GoodsCategory.EVENT)}
            />
          ) : (
            <SecondaryButton
              key={GoodsCategory.EVENT}
              text={t('GoodsCategory.event')}
              disabled={false}
              icon={null}
              leftIcon={null}
              fontSize="13px"
              onClick={() => setSelectedCategory(GoodsCategory.EVENT)}
            />
          ))}
        {!isEmpty(eventDetail.goodsByCategory.accessory) &&
          (selectedCategory === GoodsCategory.ACCESSORY ? (
            <PrimaryButton
              key={GoodsCategory.ACCESSORY}
              text={t('GoodsCategory.accessory')}
              disabled={false}
              icon={null}
              fontSize="13px"
              onClick={() => setSelectedCategory(GoodsCategory.ACCESSORY)}
            />
          ) : (
            <SecondaryButton
              key={GoodsCategory.ACCESSORY}
              text={t('GoodsCategory.accessory')}
              disabled={false}
              icon={null}
              leftIcon={null}
              fontSize="13px"
              onClick={() => setSelectedCategory(GoodsCategory.ACCESSORY)}
            />
          ))}
      </div>
      <div className="grid grid-cols-3 md:grid-cols-4 lg:grid-cols-4 justify-self-center gap-[12px] w-full">
        {selectedCategory === GoodsCategory.ALL
          ? eventDetail.goodsByCategory.getAllGoods().map((goods) => {
              return <GoodsItemBox key={goods.key} goods={goods} />;
            })
          : eventDetail.goodsByCategory.getGoodsByCategory(selectedCategory).map((goods) => {
              return <GoodsItemBox key={goods.key} goods={goods} />;
            })}
      </div>

      {eventDetail?.category !== 'billboard_ad' && (
        <ReportLocationEventBox onClick={() => setIsOpenReportDrawer(true)} />
      )}

      {isOpenReportDrawer && (
        <ReportLocationEventBottomDrawer
          eventId={eventDetail.eventId}
          isOpen={isOpenReportDrawer}
          onReport={() => showReportToastMessage()}
          onClose={() => setIsOpenReportDrawer(false)}
        />
      )}
    </div>
  );
};

const GoodsItemBox = ({ goods }: { goods: IGoodsItem }) => {
  const giftType = goods.giftTypes?.find(type => type !== 'base') ?? 'base';
  const formattedGiftType = snakeToCamel(giftType);

  return (
    <div
      key={goods.key}
      className="flex flex-col relative items-center justify-center p-4 bg-surface-light dark:bg-surface-dark rounded-xl"
    >
      <div className="absolute top-0 left-0 p-[8px]">
        {
          formattedGiftType === "base" ? (
            <div className="text-[10px] text-sub-light-1 dark:text-sub-dark-1">
              {t('GoodsGiftBadge.base')}
            </div>
          ) : (
            <div className="text-[10px] text-pure-white bg-gradient-single-red rounded-full px-[4px] py-[2px]">
              {t(`GoodsGiftBadge.${formattedGiftType}`)}
            </div>
          )
        }
      </div>
      <img
        src={`/assets/icons/goods/Goods.${goods.key.toUpperCase()}.png`}
        alt={`Goods.${goods.key}`}
        className="w-[60px] h-[60px]"
      />
      <div className="text-center text-[13px] leading-[16px] h-[32px] text-main-light-2 dark:text-main-dark-2 content-center">
        {goods.name}
      </div>
    </div>
  )
}

export default EventGoodsByCategory;
